import { Badge, BodyText, Collapsible } from "@/components/ui";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { useMemo } from "react";
import { type ChangeEntry, computeDelta, flattenDelta } from "../utils/diff";
import {
  type FhirResource,
  formatFhirValue,
  humanizePath,
  isSimpleFhirValue,
} from "../utils/fhir";
import { JsonPretty } from "./JsonPretty";

interface VersionDiffViewProps {
  /** Older version (undefined when `after` is the initial version). */
  before?: FhirResource;
  after: FhirResource;
}

// Diffed against this for the initial version, so every field reads as "Added".
const EMPTY_BASELINE: Record<string, unknown> = {};

/** Drop bookkeeping fields that only add noise to a version diff. */
const stripNoise = (resource: FhirResource): Record<string, unknown> => {
  const { meta: _meta, text: _text, ...rest } = resource;
  return rest;
};

const KIND_BADGE: Record<
  ChangeEntry["kind"],
  { label: string; variant: "success" | "error" | "warning" }
> = {
  added: { label: "Added", variant: "success" },
  removed: { label: "Removed", variant: "error" },
  modified: { label: "Changed", variant: "warning" },
};

const pathToString = (path: (string | number)[]): string =>
  path
    .map((segment) =>
      typeof segment === "number" ? `[${segment}]` : `.${segment}`,
    )
    .join("")
    .replace(/^\./, "");

/**
 * Renders the difference between two resource versions as a single table with
 * both a clinical (humanized) and an engineering (raw path + JSON) column, so
 * either audience can read the same row.
 */
export const VersionDiffView = ({ before, after }: VersionDiffViewProps) => {
  // For the initial version there's no predecessor — diff against an empty
  // baseline so every field surfaces as "Added" and the user sees v1's content.
  const delta = useMemo(
    () =>
      computeDelta(
        before ? stripNoise(before) : EMPTY_BASELINE,
        stripNoise(after),
      ),
    [before, after],
  );
  const changes = useMemo(() => flattenDelta(delta), [delta]);
  const isInitial = !before;

  if (changes.length === 0) {
    return (
      <BodyText size="sm" textColor="secondary">
        No field-level changes between these versions (metadata only).
      </BodyText>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {isInitial && (
        <BodyText size="sm" textColor="secondary">
          Initial version — all fields shown as added.
        </BodyText>
      )}

      <div className="min-w-0 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Change</TableHead>
              <TableHead>Clinical</TableHead>
              <TableHead>Engineering</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {changes.map((change) => (
              <TableRow
                key={`${change.kind}:${pathToString(change.path)}`}
                className="align-top"
              >
                <TableCell className="align-top">
                  <Badge variant={KIND_BADGE[change.kind].variant}>
                    {KIND_BADGE[change.kind].label}
                  </Badge>
                </TableCell>
                <TableCell className="min-w-0 align-top">
                  <div className="flex min-w-0 flex-col gap-1">
                    <BodyText size="sm" bold>
                      {humanizePath(change.path)}
                    </BodyText>
                    <ClinicalValues change={change} />
                  </div>
                </TableCell>
                <TableCell className="min-w-0 align-top">
                  <div className="flex min-w-0 flex-col gap-1 font-mono text-body-xs">
                    <span className="break-words text-secondary">
                      {pathToString(change.path)}
                    </span>
                    {change.before !== undefined && (
                      <span className="whitespace-pre-wrap break-words text-error">
                        - {JSON.stringify(change.before)}
                      </span>
                    )}
                    {change.after !== undefined && (
                      <span className="whitespace-pre-wrap break-words text-success">
                        + {JSON.stringify(change.after)}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Collapsible title="Raw jsondiffpatch delta">
        <JsonPretty value={delta} className="mt-2" />
      </Collapsible>
    </div>
  );
};

/** Readable (clinical) rendering of a change: one-liner if simple, else JSON. */
const ClinicalValues = ({ change }: { change: ChangeEntry }) => {
  const beforeSimple =
    change.before === undefined || isSimpleFhirValue(change.before);
  const afterSimple =
    change.after === undefined || isSimpleFhirValue(change.after);

  if (change.kind === "modified" && beforeSimple && afterSimple) {
    return (
      <BodyText size="sm" className="break-words">
        <span className="text-error line-through">
          {formatFhirValue(change.before)}
        </span>
        <span className="mx-2 text-secondary">→</span>
        <span className="text-success">{formatFhirValue(change.after)}</span>
      </BodyText>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-2">
      {change.before !== undefined && (
        <ValueBlock
          label={change.kind === "modified" ? "Before" : undefined}
          value={change.before}
          tone="removed"
        />
      )}
      {change.after !== undefined && (
        <ValueBlock
          label={change.kind === "modified" ? "After" : undefined}
          value={change.after}
          tone="added"
        />
      )}
    </div>
  );
};

const ValueBlock = ({
  label,
  value,
  tone,
}: {
  label?: string;
  value: unknown;
  tone: "added" | "removed";
}) => (
  <div className="flex min-w-0 flex-col gap-1">
    {label && (
      <BodyText size="xs" textColor="secondary">
        {label}
      </BodyText>
    )}
    {isSimpleFhirValue(value) ? (
      <BodyText
        size="sm"
        className={
          tone === "removed"
            ? "break-words text-error line-through"
            : "break-words text-success"
        }
      >
        {formatFhirValue(value)}
      </BodyText>
    ) : (
      <JsonPretty value={value} tone={tone} />
    )}
  </div>
);
